/**
 * LastReportHighlightCard component
 * Displays a 'Your last report' hero card on the student home page.
 *
 * Implements US-020 T3 (AC-1).
 * Redesign (2026-06): restyled to align with the dashboard redesign (header strip,
 * green/amber highlight boxes, warm CTA). ALL copy, roles, aria-labels and the
 * navigation target are preserved verbatim ("Your last report", "You were great at: ",
 * "Let's practise: ", "View Full Report" / aria "View full report", the card aria-label,
 * and navigate(/session/:sessionId)) so the US-020 tests remain valid.
 *
 * CCM-03: No price, coin, balance, upgrade, or purchase text anywhere in this component.
 * TO-LL-013: Navigation target is built exclusively from server-derived authenticated data.
 */

import { useNavigate } from 'react-router-dom';
import { Check, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/StarRating';
import type { LastReport } from '@/types/dashboard';

export interface LastReportHighlightCardProps {
  /** Last report data from the dashboard API. Parent only renders this when non-null. */
  lastReport: LastReport;
}

export function LastReportHighlightCard({ lastReport }: LastReportHighlightCardProps) {
  const navigate = useNavigate();

  const firstStrength = lastReport.strengths[0];
  const firstImprovement = lastReport.improvements[0];
  const displayTitle = lastReport.passageTitle || 'Your last reading';

  return (
    <div
      className="mb-4 overflow-hidden rounded-2xl border border-tutor-200 bg-white shadow-md md:mb-6"
      aria-label="Your last report highlight card"
    >
      {/* Header strip */}
      <div className="flex h-12 items-end bg-gradient-to-r from-tutor-200 to-tutor-300 px-5 pb-2">
        <h3 className="font-display text-base font-semibold text-tutor-800">Your last report</h3>
      </div>

      <div className="space-y-3 p-5">
        {/* Passage title + star rating */}
        <div className="flex flex-wrap items-center gap-3">
          <p
            className="truncate font-display text-base font-semibold text-otter-earth-800"
            title={displayTitle}
          >
            {displayTitle}
          </p>
          <StarRating rating={lastReport.starRating} size="md" />
        </div>

        {/* Strength — green box; omitted when strengths array is empty */}
        {firstStrength && (
          <div className="flex items-start gap-2 rounded-xl border border-student-200 bg-student-50 p-3">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-student-600" aria-hidden="true" />
            <span className="text-sm text-otter-earth-700">
              <span className="font-semibold text-student-700">You were great at: </span>
              {firstStrength}
            </span>
          </div>
        )}

        {/* Improvement — amber box; omitted when improvements array is empty */}
        {firstImprovement && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <Target className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
            <span className="text-sm text-otter-earth-700">
              <span className="font-semibold text-amber-700">{"Let's practise: "}</span>
              {firstImprovement}
            </span>
          </div>
        )}

        {/* View Full Report CTA — warm; navigates to the read-only session/report view. */}
        <Button
          variant="warm"
          size="lg"
          className="mt-1 h-12 w-full"
          aria-label="View full report"
          onClick={() => navigate(`/session/${lastReport.sessionId}`)}
        >
          View Full Report
        </Button>
      </div>
    </div>
  );
}
